<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="q-pa-md">
        <div class="row q-col-gutter-x-lg items-center justify-end">
            <div class="col">
                <q-btn
                    class="btn-custom"
                    color="secondary"
                    flat
                    dense
                    :disable="isNewAddress"
                    :ripple="false"
                    @click="addNewAddress()"
                >
                    <q-icon name="add" size="20px" class="q-mr-sm" />
                    <span>{{ $t('address.new') }}</span>
                </q-btn>
            </div>
            <div class="col-auto">
                <span class="text-h6"
                    >{{ $t('address.saved_avaliable') }}:</span
                >
            </div>
            <div class="col-auto">
                <q-select
                    v-model="addressSelected"
                    :options="addressOptions"
                    :loading="isFetchingAddresses"
                    option-value="id"
                    :option-label="(option) => option.title || ''"
                    map-options
                    dense
                    outlined
                    bg-color="white"
                    @input="mapSelectedAddress()"
                >
                </q-select>
            </div>
        </div>
        <q-separator class="q-my-md"></q-separator>
        <div class="row q-col-gutter-x-sm">
            <div class="col q-pb-lg">
                <q-input
                    v-model="address.title"
                    outlined
                    clearable
                    counter
                    maxlength="250"
                    :label="$t('general.title')"
                    bg-color="white"
                    class="q-mr-lg"
                    :error-message="titleErrors"
                    :error="titleErrors != ''"
                    @input="$v.address.title.$touch()"
                    @blur="$v.address.title.$touch()"
                />
            </div>
            <div class="col-auto q-pb-lg">
                <div class="text-caption q-mx-sm">
                    {{ $t('general.preferred') }}
                </div>
                <q-toggle
                    v-model="address.is_preferred"
                    color="primary"
                    keep-color
                />
            </div>
            <div class="col-12 q-pb-lg">
                <q-input
                    v-model="address.street"
                    outlined
                    clearable
                    counter
                    maxlength="35"
                    :label="$t('general.street')"
                    bg-color="white"
                />
            </div>
            <div class="col-12 q-pb-lg">
                <q-input
                    v-model="address.city"
                    outlined
                    clearable
                    counter
                    maxlength="100"
                    :label="$t('general.city')"
                    bg-color="white"
                />
            </div>
            <div class="col-6">
                <q-input
                    v-model="address.state"
                    outlined
                    clearable
                    counter
                    maxlength="35"
                    :label="$t('general.state')"
                    bg-color="white"
                />
            </div>
            <div class="col-6">
                <q-input
                    v-model="address.zip_code"
                    outlined
                    clearable
                    counter
                    maxlength="20"
                    :label="$t('general.zip_code')"
                    bg-color="white"
                />
            </div>
            <div class="col-12 q-mt-md text-right">
                <q-btn
                    :disable="address.is_preferred || address.id == null"
                    class="q-my-md q-ml-sm"
                    color="white"
                    text-color="black"
                    @click="removeAddress()"
                    >{{ $t('actions.remove') }}
                </q-btn>
                <q-btn
                    class="q-my-md q-ml-sm"
                    color="white"
                    text-color="secondary "
                    @click="saveAddress(address)"
                    >{{ $t('actions.save_address') }}</q-btn
                >
            </div>
        </div>
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import translate from '@/local-shared/mixins/translate'
import usersService from '@/services/modules/users'
import taxAddressesService from '@/services/modules/taxAddresses'
import notify from '@/local-shared/mixins/notify'
import store from '@/store'
import { required } from 'vuelidate/lib/validators'

const loadingKeys = {
    fetchingAddresses: 'fetching-adresses',
    fetchingUser: 'fetching-user',
}

export default {
    name: 'MyTaxAddress',

    components: {
        PageLoading,
    },

    mixins: [translate, notify],

    data() {
        return {
            user: {},
            addressSelected: {},
            addressOptions: [],
            address: {
                id: null,
                title: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                is_preferred: false,
            },
            isNewAddress: false,
        }
    },

    validations: {
        address: {
            title: {
                required,
            },
        },
    },

    computed: {
        storedUser() {
            return store.getters['session/user']
        },

        isLoading() {
            return this.isFetchingUser || this.isFetchingAddresses
        },

        isFetchingUser() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingUser
            ).length
        },

        isFetchingAddresses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingAddresses
            ).length
        },

        titleErrors() {
            let errors = ''
            if (!this.$v.address.title.$dirty) {
                return errors
            }
            if (!this.$v.address.title.required)
                errors = errors.concat(
                    this.$t('validations.tax_address_title_is_required')
                )
            return errors
        },
    },

    async created() {
        await this.fetchUser()
        await this.fetchAddresses()
    },

    methods: {
        addNewAddress() {
            this.address = {
                id: null,
                title: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                is_preferred: false,
            }
            this.addressSelected = this.address
            this.isNewAddress = true
        },

        mapSelectedAddress() {
            this.address = this.addressSelected
            this.isNewAddress = false
        },

        async fetchUser() {
            this.loadingAdd(loadingKeys.fetchingUser)

            let parameters = {
                filter: {
                    id: this.storedUser.id,
                },
                relations: {
                    affiliations: true,
                    userRoles: true,
                },
            }

            let response = await usersService.retrieve(parameters)

            if (response.status === 200) {
                this.user = response.data.results[0]
                this.user.summary = this.user.summary || '' // prevent Null value from database
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.fetchingUser)
        },

        async fetchAddresses() {
            this.loadingAdd(loadingKeys.fetchingAddresses)

            let parameters = {
                filter: {},
            }

            if (this.user.userRoles.name === 'client') {
                parameters.relations = { client: true }
                parameters.filter.clientTaxEnabled = true
                parameters.filter.entityId = this.user.affiliations[0].client_id
            }
            if (this.user.userRoles.name === 'student') {
                parameters.relations = { user: true }
                parameters.filter.userTaxEnabled = true
                parameters.filter.entityId = this.user.id
            }

            let response = await taxAddressesService.retrieve(parameters)

            if (response.status === 200) {
                let results = response.data.results
                let addressResults = []
                results.forEach((addressResult) =>
                    addressResults.push(
                        addressResult?.taxAddress
                            ? addressResult.taxAddress
                            : {
                                  id: null,
                                  title: '',
                                  street: '',
                                  city: '',
                                  state: '',
                                  zip_code: '',
                                  is_preferred: false,
                              }
                    )
                )
                this.addressOptions = addressResults
                if (!this.addressOptions.length) {
                    this.addNewAddress()
                } else {
                    this.address = this.addressOptions[0]
                    this.addressSelected = this.address
                }
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingAddresses)
        },

        mapAddress(address) {
            return {
                title: address.title,
                street: address.street,
                city: address.city,
                state: address.state,
                zip_code: address.zip_code,
                is_preferred: address.is_preferred,
            }
        },

        async saveAddress(address) {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('validations.required_fields_missing'),
                })
                return
            }

            this.loadingAdd(loadingKeys.fetchingAddresses)

            let parameters = {
                data: [this.mapAddress(address)],
            }

            if (this.user.userRoles.name === 'client') {
                parameters.relations = { client: true }
                parameters.data[0].entityId = this.user.affiliations[0].client_id
            }
            if (this.user.userRoles.name === 'student') {
                parameters.relations = { user: true }
                parameters.data[0].entityId = this.user.id
            }

            let response

            if (address.id) {
                parameters.data[0].id = address.id
                response = await taxAddressesService.update(parameters)
            } else {
                response = await taxAddressesService.create(parameters)
            }

            if (response.status === 200) {
                this.isNewAddress = false
                this.fetchAddresses()
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.address_saved'),
                })
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.fetchingAddresses)
        },

        async removeAddress() {
            this.loadingAdd(loadingKeys.fetchingAddresses)

            let parameters = {
                data: [{ id: this.address.id }],
            }

            let response = await taxAddressesService.delete(parameters)

            if (response.status === 200) {
                this.addressTab = 'default'
                this.fetchAddresses()
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.address_removed'),
                })
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.fetchingAddresses)
        },
    },
}
</script>
